import { useState, type FormEvent } from "react";
import { useSiteSearch } from "../../api/boq";
import { useAddStop, type PlanDay } from "../../api/routing";
import { useDebounced } from "../../hooks/useDebounced";
import { PositionField } from "./ChangeStop";

/** Add a site to a day when the City asks for one: any site, found by name,
 * address, works order or RS number, placed before one of the day's stops or
 * at the end, with a reason. The server refuses a site already on the plan,
 * planned elsewhere, or held by the scheduling gate, and says why. */
export default function AddStop({ planId, day, onDone }: { planId: number; day: PlanDay; onDone: () => void }) {
  const add = useAddStop(planId);
  const [text, setText] = useState("");
  const [siteId, setSiteId] = useState("");
  const [before, setBefore] = useState("");
  const [reason, setReason] = useState("");
  const q = useDebounced(text.trim(), 250);
  const found = useSiteSearch(q);
  const rows = found.data?.rows ?? [];
  const why = reason.trim();

  function submit(event: FormEvent) {
    event.preventDefault();
    add.mutate(
      {
        dayIndex: day.day_index,
        site_id: Number(siteId),
        before_stop_id: before === "" ? null : Number(before),
        reason: why,
      },
      { onSuccess: onDone },
    );
  }

  return (
    <form className="plan-stop-form" onSubmit={submit} aria-label={`Add a site to Day ${day.day_index + 1}`}>
      <label className="field">
        Find a site
        <input
          type="search"
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            setSiteId("");
          }}
          placeholder="Name, address, works order or RS number"
        />
      </label>
      <label className="field">
        Site
        <select value={siteId} onChange={(e) => setSiteId(e.target.value)} required>
          <option value="">{q.length < 2 ? "Type at least two letters…" : found.isPending ? "Searching…" : `${rows.length} found`}</option>
          {rows.map((row) => (
            <option key={row.id} value={row.id}>
              {row.address ? `${row.name} — ${row.address}` : row.name}
            </option>
          ))}
        </select>
      </label>
      <PositionField day={day} value={before} onChange={setBefore} />
      <label className="field">
        Why
        <input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} required />
      </label>
      <button type="submit" className="btn btn-primary" disabled={add.isPending || siteId === "" || why === ""}>
        Add
      </button>
      {found.data?.truncated ? <p className="notice warn">More sites match; type more of the name.</p> : null}
      {found.isError ? <p className="notice bad">{found.error.message}</p> : null}
      {add.isError ? <p className="notice bad">{add.error.message}</p> : null}
    </form>
  );
}
